/**
 * Four-band geo pricing for Lern (Solo) and Edu (teacher) plans.
 *
 * Yearly = default checkout (billed once per year).
 * Monthly = secondary tab; priced higher so yearly is the better deal (~50% off at tier 1).
 *
 * Tier assignment: ISO 3166-1 alpha-2 billing / geo country codes.
 * Unlisted countries default to tier 4.
 *
 * Microstates & low-traffic territories (BM, KY, …) are assigned by GDP/capita band:
 * tier 1 ≈ high-income Western (~≥$50k PPP), tier 2 ≈ upper-middle / small rich (~$25–50k).
 *
 * Display amounts are synced from Stripe at build time into
 * `pricing-tier-amounts.generated.js` (see scripts/sync-stripe-paywall-prices.mjs).
 */

import generatedPricingAmounts from "./pricing-tier-amounts.generated.js";

/** @typedef {1 | 2 | 3 | 4} PricingTierId */

/**
 * @typedef {object} PricingTierAmounts
 * @property {string} label
 * @property {number} lernYearly Billed yearly (EUR)
 * @property {number} eduYearly
 * @property {number} lernMonthly Billed monthly (EUR)
 * @property {number} eduMonthly
 */

/** @type {Record<PricingTierId, PricingTierAmounts>} */
const PRICING_TIER_AMOUNTS_FALLBACK = {
  1: {
    label: "Western / high-income",
    lernYearly: 60,
    eduYearly: 120,
    lernMonthly: 10,
    eduMonthly: 20,
  },
  2: {
    label: "Central & Southern EU (core)",
    lernYearly: 36,
    eduYearly: 96,
    lernMonthly: 8,
    eduMonthly: 15,
  },
  3: {
    label: "Emerging / converting markets",
    lernYearly: 24,
    eduYearly: 60,
    lernMonthly: 5,
    eduMonthly: 10,
  },
  4: {
    label: "Volume / low-conversion",
    lernYearly: 12,
    eduYearly: 24,
    lernMonthly: 3,
    eduMonthly: 5,
  },
};

/**
 * @param {number | undefined} generated
 * @param {number} fallback
 */
function pickPricingAmount(generated, fallback) {
  return typeof generated === "number" && generated > 0 ? generated : fallback;
}

/**
 * @returns {Record<PricingTierId, PricingTierAmounts>}
 */
function buildPricingTierAmounts() {
  const generated = generatedPricingAmounts?.amounts || {};
  /** @type {Record<PricingTierId, PricingTierAmounts>} */
  const out = {};
  for (const id of /** @type {PricingTierId[]} */ ([1, 2, 3, 4])) {
    const fallback = PRICING_TIER_AMOUNTS_FALLBACK[id];
    const fromGenerated = generated[String(id)] || {};
    out[id] = {
      label: fallback.label,
      lernMonthly: pickPricingAmount(fromGenerated.lernMonthly, fallback.lernMonthly),
      lernYearly: pickPricingAmount(fromGenerated.lernYearly, fallback.lernYearly),
      eduMonthly: pickPricingAmount(fromGenerated.eduMonthly, fallback.eduMonthly),
      eduYearly: pickPricingAmount(fromGenerated.eduYearly, fallback.eduYearly),
    };
  }
  return out;
}

/** @type {Record<PricingTierId, PricingTierAmounts>} */
export const PRICING_TIER_AMOUNTS = buildPricingTierAmounts();

/**
 * Monthly equivalent of a yearly price (for “€X/mo” display).
 * @param {number} yearlyEur
 * @returns {number}
 */
export function yearlyToMonthlyDisplay(yearlyEur) {
  return yearlyEur / 12;
}

/**
 * Rounded “save X%” vs paying monthly for 12 months.
 * @param {number} monthlyEur
 * @param {number} yearlyEur
 * @returns {number} 0–100 integer
 */
export function yearlySavingsPercent(monthlyEur, yearlyEur) {
  const fullYearMonthly = monthlyEur * 12;
  if (fullYearMonthly <= 0) return 0;
  return Math.round((1 - yearlyEur / fullYearMonthly) * 100);
}

/**
 * @param {PricingTierAmounts} amounts
 * @returns {PricingTierAmounts & {
 *   lernYearlyMonthlyDisplay: number;
 *   eduYearlyMonthlyDisplay: number;
 *   lernYearlySavingsPercent: number;
 *   eduYearlySavingsPercent: number;
 *   eduToLernYearlyRatio: number;
 * }}
 */
export function withPricingDisplayFields(amounts) {
  return {
    ...amounts,
    lernYearlyMonthlyDisplay: yearlyToMonthlyDisplay(amounts.lernYearly),
    eduYearlyMonthlyDisplay: yearlyToMonthlyDisplay(amounts.eduYearly),
    lernYearlySavingsPercent: yearlySavingsPercent(amounts.lernMonthly, amounts.lernYearly),
    eduYearlySavingsPercent: yearlySavingsPercent(amounts.eduMonthly, amounts.eduYearly),
    eduToLernYearlyRatio: amounts.eduYearly / amounts.lernYearly,
  };
}

/** @type {Record<string, PricingTierId>} */
export const PRICING_TIER_BY_COUNTRY = {
  // Tier 1 — Western / high-income (former €60 Lern / €96 Edu band)
  AD: 1,
  AE: 1,
  AT: 1,
  AU: 1,
  AX: 1,
  BE: 1,
  BM: 1,
  CA: 1,
  CH: 1,
  DE: 1,
  DK: 1,
  FI: 1,
  FO: 1,
  FR: 1,
  GB: 1,
  GI: 1,
  GG: 1,
  GL: 1,
  IE: 1,
  IM: 1,
  IS: 1,
  JE: 1,
  JP: 1,
  KY: 1,
  LI: 1,
  LU: 1,
  MC: 1,
  MT: 1,
  NL: 1,
  NO: 1,
  NZ: 1,
  SE: 1,
  US: 1,
  VA: 1,
  VG: 1,

  // Tier 2 — Central EU + Southern EU + wealthy Asia (gentle uplift from old €24; see analytics)
  AW: 2,
  BN: 2,
  CY: 2,
  CW: 2,
  CZ: 2,
  EE: 2,
  ES: 2,
  GR: 2,
  HK: 2,
  HR: 2,
  HU: 2,
  IT: 2,
  KR: 2,
  LT: 2,
  LV: 2,
  MO: 2,
  NC: 2,
  PF: 2,
  PL: 2,
  PT: 2,
  SG: 2,
  SI: 2,
  SK: 2,
  SM: 2,
  SX: 2,
  TC: 2,
  TW: 2,

  // Tier 3 — Emerging markets with meaningful Lern/Edu sales
  AL: 3,
  AM: 3,
  AR: 3,
  BA: 3,
  BH: 3,
  BG: 3,
  BO: 3,
  BR: 3,
  BY: 3,
  CL: 3,
  CN: 3,
  CO: 3,
  CR: 3,
  DO: 3,
  EC: 3,
  FK: 3,
  GE: 3,
  GH: 3,
  GT: 3,
  GY: 3,
  HN: 3,
  ID: 3,
  IL: 3,
  JM: 3,
  JO: 3,
  KE: 3,
  KG: 3,
  KW: 3,
  KZ: 3,
  LB: 3,
  LK: 3,
  MA: 3,
  MD: 3,
  ME: 3,
  MK: 3,
  MN: 3,
  MX: 3,
  MY: 3,
  NI: 3,
  OM: 3,
  PA: 3,
  PE: 3,
  PH: 3,
  PR: 3,
  PY: 3,
  QA: 3,
  RO: 3,
  RS: 3,
  SA: 3,
  SV: 3,
  SR: 3,
  TH: 3,
  TJ: 3,
  TR: 3,
  TT: 3,
  UA: 3,
  UY: 3,
  UZ: 3,
  XK: 3,
  ZA: 3,

  // Tier 4 — High traffic, near-zero conversion (old affordable underperformers)
  AF: 4,
  AZ: 4,
  BD: 4,
  CM: 4,
  DZ: 4,
  EG: 4,
  ET: 4,
  IN: 4,
  IQ: 4,
  IR: 4,
  KH: 4,
  LA: 4,
  LY: 4,
  MM: 4,
  NG: 4,
  NP: 4,
  PK: 4,
  PS: 4,
  RU: 4,
  SD: 4,
  SN: 4,
  SO: 4,
  SY: 4,
  TG: 4,
  TN: 4,
  VE: 4,
  VN: 4,
  YE: 4,
};

/** Default geo band when a country is not in {@link PRICING_TIER_BY_COUNTRY}. */
export const DEFAULT_PRICING_TIER = 4;

const DEFAULT_TIER = DEFAULT_PRICING_TIER;

/**
 * @param {string | null | undefined} countryCode ISO 3166-1 alpha-2
 * @returns {PricingTierId}
 */
export function resolvePricingTier(countryCode) {
  const code = String(countryCode || "")
    .trim()
    .toUpperCase();
  if (!code) return DEFAULT_TIER;
  return PRICING_TIER_BY_COUNTRY[code] || DEFAULT_TIER;
}

/**
 * @param {string | null | undefined} countryCode
 * @returns {typeof PRICING_TIER_AMOUNTS[PricingTierId]}
 */
export function resolvePricingTierAmounts(countryCode) {
  return PRICING_TIER_AMOUNTS[resolvePricingTier(countryCode)];
}

/**
 * Tier amounts plus display helpers (€/mo equiv, savings %, Edu÷Lern).
 * @param {string | null | undefined} countryCode
 */
export function resolvePricingTierDisplay(countryCode) {
  return withPricingDisplayFields(resolvePricingTierAmounts(countryCode));
}

function countriesForTier(tier) {
  return Object.entries(PRICING_TIER_BY_COUNTRY)
    .filter(([, t]) => t === tier)
    .map(([c]) => c)
    .sort();
}

/** Country codes per tier (sorted). */
export const PRICING_TIER_COUNTRY_LISTS = {
  1: countriesForTier(1),
  2: countriesForTier(2),
  3: countriesForTier(3),
  4: countriesForTier(4),
};
